import { ChevronDown } from 'lucide-react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.tsx'
import { cn } from '@/lib/utils.ts'
import { Button } from './ui/button'
import { NavigationMenu, NavigationMenuItem, NavigationMenuList } from './ui/navigation-menu'
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover'

import { NavButton } from '@/components/NavButton.tsx'
import { NotificationList } from '@/components/NotificationList.tsx'
import { Profile } from '@/components/Profile.tsx'
import { SearchInput } from '@/components/SearchInput.tsx'
import { UserAvatar } from '@/components/UserAvatar.tsx'
import { useUserContext } from '@/contexts/UserContext.tsx'
import { useQueryProjectMeta } from '@/requests/queries/useQueryProject.ts'
import { useQueryProjects } from '@/requests/queries/useQueryProjects.ts'
import { useNotifications } from '@/requests/subscriptions/useNotifications.ts'

type Props = {
  className?: string
}

export const Header = ({ className }: Props) => {
  const { projectMeta } = useQueryProjectMeta(false)
  const { projectsMeta } = useQueryProjects()
  const { notifications, notificationCount, read: readNotifications } = useNotifications()
  const location = useLocation()

  const navigate = useNavigate()
  const { user } = useUserContext()

  const isProjectPage = location.pathname.split('/').length === 3
  const isProjectsPage = location.pathname === '/projects'

  return (
    <header className={cn('flex flex-col bg-surface-primary z-10 h-fit w-full overflow-x-scroll', className)}>
      <div
        className={cn('flex items-center justify-between min-[1000px]:h-[60px] min-[1000px]:flex-row', {
          'max-[1000px]:grid max-[1000px]:grid-cols-[1fr_1fr] grid-rows-[60px_40px]': !isProjectsPage,
          'max-[1000px]:h-[60px]': isProjectsPage,
        })}
      >
        <div className="flex items-center gap-2 h-10 pl-4 pr-6">
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: logo click */}
          <img
            className="h-5 w-[22px] object-contain cursor-pointer"
            alt="Logo Symbol"
            src="/images/tb-logo-light.svg"
            onClick={() => navigate('/projects')}
          />
          {projectMeta ? (
            <DropdownMenu>
              <DropdownMenuTrigger className="group flex items-center gap-1 outline-none">
                <span className="block font-EN-KR-sub-heading-bold text-contents-onDarkPrimary">
                  {projectMeta.name}
                </span>
                <ChevronDown className="w-4 h-4 text-contents-accent transition-transform duration-200 group-data-[state=open]:rotate-180 text-[#10A9FF] stroke-[4]" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="p-0 rounded-sm min-w-[560px] border-none overflow-hidden"
                align="start"
                alignOffset={-30}
              >
                <DropdownMenuLabel className="p-0">
                  <div className="flex flex-col items-start gap-2 px-0 py-2 bg-surface-secondary">
                    <div className="flex items-center justify-between pl-4 pr-2 py-0 w-full">
                      <h1 className="font-EN-KR-body1-bold text-contents-onDarkPrimary text-[length:var(--EN-KR-body1-bold-font-size)] tracking-[var(--EN-KR-body1-bold-letter-spacing)] leading-[var(--EN-KR-body1-bold-line-height)]">
                        My Projects
                      </h1>
                      <Button
                        variant="ghost"
                        className="h-8 bg-interactive-secondary text-contents-onDarkPrimary hover:stateLayer-onLight-enabled hover:text-contents-onDarkPrimary rounded-xs"
                      >
                        <NavLink to="/projects">
                          <span className="font-EN-KR-body2-bold text-[length:var(--EN-KR-body2-bold-font-size)] tracking-[var(--EN-KR-body2-bold-letter-spacing)] leading-[var(--EN-KR-body2-bold-line-height)]">
                            Go to Dashboard
                          </span>
                        </NavLink>
                      </Button>
                    </div>
                  </div>
                </DropdownMenuLabel>
                {projectsMeta.map((project: ProjectMeta) => (
                  <NavLink key={project.id} to={`/project/${project.id}`}>
                    <DropdownMenuItem className="cursor-pointer text-contents-tertiary bg-stateLayer-onLight-enabled rounded-none font-EN-KR-body1-regular px-[var(--spacing-contentsArea-side-padding,16px)] py-[var(--spacing-contentsArea-sm,8px)]">
                      {project.name}
                    </DropdownMenuItem>
                  </NavLink>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <img className="h-[15px] w-[120px] object-contain" alt="Logo Type" src="/images/tb-logo-text-white.svg" />
          )}
        </div>

        {projectMeta && (
          <NavigationMenu className="min-[1000px]:flex-1 min-[1000px]:flex min-[1000px]:justify-center max-[1000px]:col-span-2 max-[1000px]:order-3 max-[1000px]:min-w-full max-[1000px]:max-w-full max-[1000px]:h-10 min-[1000px]:py-2.5 border-0 max-[1000px]:border-t border-border-default-tertiary">
            <NavigationMenuList className="flex items-center justify-center gap-8">
              <NavigationMenuItem>
                <NavButton text="Stories" path={`/project/${projectMeta.id}`} />
              </NavigationMenuItem>
              <NavigationMenuItem>
                <NavButton text="Analytics" path={`/project/${projectMeta.id}/analytics`} />
              </NavigationMenuItem>
              <NavigationMenuItem>
                <NavButton text="Members" path={`/project/${projectMeta.id}/members`} />
              </NavigationMenuItem>
              <NavigationMenuItem>
                <NavButton text="Settings" path={`/project/${projectMeta.id}/settings`} />
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
        )}

        <div className="flex flex-row justify-end items-center gap-4 h-10 relative pr-4">
          {projectMeta && isProjectPage && <SearchInput />}

          <Popover>
            <PopoverTrigger asChild>
              <div
                data-count={notificationCount}
                className={cn(
                  'relative after:absolute after:top-[4px] after:right-[4px] after:min-h-[14px] after:min-w-[14px] after:translate-x-1/2 after:-translate-y-1/2 cursor-pointer',
                  {
                    'after:rounded-full after:bg-[#00bb96] after:px-1 after:text-xs after:font-EN-KR-caption-regular after:text-white after:content-[attr(data-count)] after:grid after:place-items-center':
                      notificationCount,
                  },
                )}
              >
                <UserAvatar member={user} size="md" />
              </div>
            </PopoverTrigger>
            <PopoverContent
              side="top"
              align="end"
              sideOffset={-44}
              alignOffset={-9}
              className="bg-surface-backgroundElevated rounded-xs w-[320px] p-0 flex flex-col justify-center items-center"
            >
              <Profile />
              <hr className="my-2 border-0 border-b border-tb-gray-100 w-[calc(100%-32px)]" />
              <NotificationList notifications={notifications} read={readNotifications} />
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </header>
  )
}
